import { FloorGroup } from "../../../interfaces/AreaSheet";
import { ReportUseGroup } from "../../../interfaces/EstimateUseGroup";
import { getNamedRangeFromWorksheet } from "../../workbook";
import { columnNames, rowNames } from "./rangeNames";
import { writeCostDenominators } from "./costDenominators";
import { ValidationError } from "../../../errors";

// add a local fallback label to avoid circular deps
const FALLBACK_FLOOR_GROUP_HEADER = "blank";

export type FloorGroupColumnInfo = {
  [key: string]: { total: number; gsf?: number };
};

/**
 * Fills a floor-group column block, one column per building plus its $/SF column when the sheet has
 * the cost denominators row, and returns where the columns landed. Shared by the SOV and estimate
 * reports.
 */
export const handleFloorGroupsWithDenominators = async (
  worksheet: Excel.Worksheet,
  floorGroups: FloorGroup[],
  dataRangeName: string,
  isSOV?: boolean,
  options?: {
    markupsRangeName?: string;
    subTotalsRangeNamePrefix?: string;
    grandTotalRangeName?: string;
    applyStyleToFirstRow?: boolean;
    /** Column block to fill; defaults to `\floor_groups`. */
    columnRangeName?: string;
  },
  useGroups?: ReportUseGroup[]
): Promise<
  | {
      floorGroupColumnIndices: FloorGroupColumnInfo;
      costDenominatorsRowIndex?: number;
      firstMarkupRowIndex?: number;
      firstRowIndex: number;
      floorGroupsRequested: boolean;
      costPerColumnsRequested: boolean;
    }
  | undefined
> => {
  const columnRangeName = `\\${options?.columnRangeName ?? columnNames.floorGroups}`;
  const firstFloorGroupColumn = await getNamedRangeFromWorksheet(worksheet, columnRangeName);
  if (!firstFloorGroupColumn) {
    return undefined;
  }

  const dataRange = await getNamedRangeFromWorksheet(worksheet, dataRangeName);
  if (!dataRange) {
    throw new ValidationError("The worksheet does not contain the required named range", { rangeName: dataRangeName });
  }

  const costDenominatorsRowRange = await getNamedRangeFromWorksheet(worksheet, rowNames.costDenominators);
  const markupDataRange = options?.markupsRangeName
    ? await getNamedRangeFromWorksheet(worksheet, options.markupsRangeName)
    : null;

  firstFloorGroupColumn.getEntireColumn().ungroup("ByColumns");

  const floorGroupDenominatorsRequested = costDenominatorsRowRange !== null;

  const firstRowRange = dataRange.getCell(0, 0);
  const firstMarkupRowRange = markupDataRange?.getCell(0, 0);
  firstRowRange.load("rowIndex");
  costDenominatorsRowRange?.load("rowIndex");
  firstFloorGroupColumn.load("columnCount, address, columnIndex");
  firstMarkupRowRange?.load("rowIndex");
  await worksheet.context.sync();

  const firstRowIndex = firstRowRange.rowIndex;
  const costDenominatorsRowIndex = costDenominatorsRowRange?.rowIndex;
  const firstMarkupRowIndex = firstMarkupRowRange?.rowIndex;

  // Compute the display column count and denominator column metadata
  let displayColumns = floorGroups.length; // at least the total column per group
  const denomByRelativeIndex: { [relativeColIndex: number]: { value: string | number; uom: string } } = {};
  let relativeCursor = 0; // relative column index within the range

  if (floorGroups.length > 0 && floorGroupDenominatorsRequested) {
    for (const fg of floorGroups) {
      // total column (advance cursor, but no denominator metadata)
      relativeCursor += 1;

      // Sum every use group in the floor group, including those with Exclude Area on, so the
      // floor group's $/SF reconciles with the per-use-group $/SF columns, which ignore that flag.
      const gsf = useGroups
        ? useGroups.filter((ug) => ug.floor_group === fg.label).reduce((sum, ug) => sum + (ug.gsf ?? 0), 0)
        : 0;
      denomByRelativeIndex[relativeCursor] = {
        value: gsf,
        uom: "GSF",
      };
      relativeCursor += 1;
      displayColumns += 1;
    }
  }

  // Always keep at least one column to avoid negative resizing
  displayColumns = Math.max(1, displayColumns);

  // Resize columns to match displayColumns
  const existingColumns = firstFloorGroupColumn.columnCount;
  const startCell = firstFloorGroupColumn.getCell(0, 0);
  startCell.format.load("columnWidth");
  startCell.load("columnIndex");
  await worksheet.context.sync();

  if (existingColumns > 1) {
    const toDelete = startCell.getOffsetRange(0, 1).getResizedRange(0, existingColumns - 2);
    toDelete.getEntireColumn().delete(Excel.DeleteShiftDirection.left);
    await worksheet.context.sync();
  }

  if (displayColumns > 1) {
    let newCols = startCell
      .getOffsetRange(0, 1)
      .getResizedRange(0, displayColumns - 2)
      .getEntireColumn();
    newCols.insert(Excel.InsertShiftDirection.right);
    await worksheet.context.sync();

    newCols = startCell
      .getOffsetRange(0, 1)
      .getResizedRange(0, displayColumns - 2)
      .getEntireColumn();
    newCols.copyFrom(startCell.getEntireColumn(), Excel.RangeCopyType.all);
    newCols.format.columnWidth = startCell.format.columnWidth;
  }
  await worksheet.context.sync();

  const updatedRange = startCell.getResizedRange(0, displayColumns - 1);

  // Replace the named range with the resized range
  worksheet.names.getItemOrNullObject(columnRangeName).delete();
  await worksheet.context.sync();
  worksheet.names.add(columnRangeName, updatedRange);

  // Header row
  const headers =
    floorGroups.length > 0
      ? floorGroups.flatMap((fg) => {
          const a = [fg.label];
          if (floorGroupDenominatorsRequested) {
            a.push("$/SF");
          }
          return a;
        })
      : [FALLBACK_FLOOR_GROUP_HEADER];

  updatedRange.values = [headers];
  await worksheet.context.sync();

  // Clear contents of floor groups column when no floor groups are present to remove #REF errors
  if (floorGroups.length === 0) {
    updatedRange.getEntireColumn().clear(Excel.ClearApplyTo.contents);
    await worksheet.context.sync();
  }

  // Build column indices mapping
  const floorGroupColumnIndices: FloorGroupColumnInfo = {};
  if (floorGroups.length > 0) {
    let columnIndex = startCell.columnIndex;
    floorGroups.forEach((floorGroup) => {
      const floorGroupTotalCol = columnIndex++;
      let gsfCol: number | undefined;

      if (floorGroupDenominatorsRequested) {
        gsfCol = columnIndex++;
      }

      floorGroupColumnIndices[floorGroup.id] = { total: floorGroupTotalCol, gsf: gsfCol };
    });
  }

  if (floorGroups.length > 0 && floorGroupDenominatorsRequested && costDenominatorsRowIndex !== undefined) {
    await writeCostDenominators({
      worksheet,
      columns: Object.keys(denomByRelativeIndex)
        .map(Number)
        .map((k) => ({ columnIndex: startCell.columnIndex + k, ...denomByRelativeIndex[k] })),
      costDenominatorsRowIndex,
      firstRowIndex,
      firstMarkupRowIndex,
      subTotalsRangeNamePrefix: options?.subTotalsRangeNamePrefix,
      grandTotalRangeName: options?.grandTotalRangeName,
      applyStyleToFirstRow: options?.applyStyleToFirstRow !== false,
      isSOV: !!isSOV,
    });
  }

  updatedRange.getEntireColumn().group("ByColumns");
  await worksheet.context.sync();

  return {
    floorGroupColumnIndices,
    costDenominatorsRowIndex,
    firstMarkupRowIndex,
    firstRowIndex,
    floorGroupsRequested: true,
    costPerColumnsRequested: floorGroupDenominatorsRequested,
  };
};
